import { useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardBody, Flex, Skeleton, Text } from '@backstage/ui';
import { useTrackHistory } from '../../hooks';
import { HistoryChartHeader } from '../history-chart-header';
import { historyChartBodyStyle } from '../history-chart-style';
import { LEVEL_COLORS, NO_LEVEL_COLOR } from './track-level-colors';

const formatDate = (dateStr: string) =>
  DateTime.fromISO(dateStr).toFormat('MMM d');

export type TrackHistoryChartCardProps = {
  trackId: string;
  levels: Array<{ ordinal: number; label: string }>;
  /**
   * When true, the entity filter on the details page is active. History is
   * always computed across all entities, so we flag it as not reflecting the
   * current filter.
   */
  isFiltered?: boolean;
};

const seriesKey = (ordinal: number) => `l${ordinal}`;

type TrackHistoryPoint = {
  date: string;
  levels: Array<{ ordinal: number; entityCount: number }>;
};

/**
 * Builds the stacked-area chart rows and the ordered list of level ordinals to
 * render. Only ordinals that exist in the current track definition (plus "No
 * Level", ordinal 0) are included; historical rows may reference ordinals from
 * levels that have since been removed, and those must not appear in the chart.
 */
export const buildTrackHistoryChartData = (
  history: TrackHistoryPoint[] | undefined,
  levels: Array<{ ordinal: number }>,
): {
  chartData: Array<Record<string, number | string>>;
  ordinals: number[];
} => {
  const points = history ?? [];
  const validOrdinals = new Set<number>([0, ...levels.map(l => l.ordinal)]);
  const ordinalSet = new Set<number>();
  for (const point of points) {
    for (const level of point.levels) {
      if (validOrdinals.has(level.ordinal)) {
        ordinalSet.add(level.ordinal);
      }
    }
  }
  const sortedOrdinals = Array.from(ordinalSet).sort((a, b) => a - b);

  const rows = points.map(point => {
    const row: Record<string, number | string> = {
      date: formatDate(point.date),
    };
    for (const ordinal of sortedOrdinals) {
      row[seriesKey(ordinal)] = 0;
    }
    for (const level of point.levels) {
      if (validOrdinals.has(level.ordinal)) {
        row[seriesKey(level.ordinal)] = level.entityCount;
      }
    }
    return row;
  });

  return { chartData: rows, ordinals: sortedOrdinals };
};

export const TrackHistoryChartCard = ({
  trackId,
  levels,
  isFiltered = false,
}: TrackHistoryChartCardProps) => {
  const [selectedDays, setSelectedDays] = useState(30);

  const {
    value: history,
    loading,
    error,
  } = useTrackHistory(trackId, selectedDays);

  const { chartData, ordinals } = useMemo(
    () => buildTrackHistoryChartData(history, levels),
    [history, levels],
  );

  const labelByOrdinal = useMemo(() => {
    const map = new Map<number, string>();
    for (const level of levels) {
      map.set(level.ordinal, level.label);
    }
    return map;
  }, [levels]);

  const colorByOrdinal = useMemo(() => {
    const map = new Map<number, string>();
    [...levels]
      .sort((a, b) => a.ordinal - b.ordinal)
      .forEach((level, index) => {
        map.set(level.ordinal, LEVEL_COLORS[index % LEVEL_COLORS.length]);
      });
    return map;
  }, [levels]);

  const labelForOrdinal = (ordinal: number) =>
    ordinal === 0
      ? 'No Level'
      : labelByOrdinal.get(ordinal) ?? `Level ${ordinal}`;

  const colorForOrdinal = (ordinal: number) =>
    ordinal === 0
      ? NO_LEVEL_COLOR
      : colorByOrdinal.get(ordinal) ?? LEVEL_COLORS[0];

  if (loading) {
    return (
      <Card style={{ height: '100%' }}>
        <CardBody>
          <Flex direction="column" gap="2">
            <Skeleton width={200} height="var(--bui-space-6)" />
            <Skeleton width="100%" height={140} />
          </Flex>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return <Text color="danger">{error.message}</Text>;
  }

  if (chartData.length === 0) {
    return (
      <Card style={{ height: '100%' }}>
        <HistoryChartHeader
          isFiltered={isFiltered}
          selectedDays={selectedDays}
          onSelectedDaysChange={setSelectedDays}
        />
        <CardBody>
          <Text color="secondary">No history available yet.</Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card style={{ height: '100%' }}>
      <HistoryChartHeader
        isFiltered={isFiltered}
        selectedDays={selectedDays}
        onSelectedDaysChange={setSelectedDays}
      />
      <CardBody>
        <Flex
          direction="column"
          gap="2"
          style={historyChartBodyStyle(isFiltered)}
        >
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip />
              <Legend />
              {[...ordinals]
                .sort((a, b) => {
                  if (a === 0) return 1;
                  if (b === 0) return -1;
                  return a - b;
                })
                .map(ordinal => {
                  const color = colorForOrdinal(ordinal);
                  return (
                    <Area
                      key={ordinal}
                      type="monotone"
                      dataKey={seriesKey(ordinal)}
                      name={labelForOrdinal(ordinal)}
                      stackId="1"
                      stroke={color}
                      fill={color}
                    />
                  );
                })}
            </AreaChart>
          </ResponsiveContainer>
        </Flex>
      </CardBody>
    </Card>
  );
};
